/**
 * @fileoverview Reverse Directory searcher for block/address lookup.
 * @module searchers/ReverseSearcher
 */

import { DbSearcher } from './DbSearcher.js';
import { preProcessQueryParseOptionsDirectives } from '../utils/jrdslquery.js';
import { parseBlockQueryString } from '../utils/nynfuncs.js';
import { SEARCH_TYPES } from '../constants.js';
import { buildEmptySearchResultStructure, buildSearchResult } from '../utils/jrfuncs.js';

/**
 * Manages Reverse Directory search functionality.
 * Looks up entries by block location using "Neighborhood:Block#" format.
 * @class
 * @extends DbSearcher
 */
export class ReverseSearcher extends DbSearcher {
    /**
     * Creates a new ReverseSearcher instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} [options={}] - Configuration options
     */
    constructor(mapApp, options = {}) {
        super(mapApp, {
            searcherName: SEARCH_TYPES.REVERSE,
            searchMethod: ReverseSearcher.prototype.searchDataForBlockQuery,
            emptyMessage: 'Click a block on the map or enter Neighborhood:Block#.',
            enableActionBarSave: true,
            enableActionBarEdit: true,
            enableActionBarRead: true,
            listerOptions: {
                contentsLabel: 'reverse search results',
                supportsIndent: true,
            },
            ...options
        });
    }




    // ==========================================
    // Search method
    // ==========================================

    /**
     * Searches for items by block location using "Neighborhood:Block#" format.
     * Parses the query into jregion and loclabel components, then matches items.
     * @param {Object} searchPack - The search pack with query (e.g., "GV25") and options
     * @returns {Promise<Object>} Search result object with results array and totalCount
     */
    async searchDataForBlockQuery(searchPack) {
        const { jregion, loclabel } = parseBlockQueryString(searchPack.query);
        if (!jregion) {
            // we often have a strange situation where user finds themselves on the Reverse search tab but think they are on the advanced tab (or white pages tab if a non-advanced user), because they clicked on the map which brought them to the reverse tab
            // when this happens, AND they hit enter, AND their search does not make sense as a reverse search, we redirect to Advanced (if enabled) or White Pages and run the search there
            if (searchPack.isEnterKey && searchPack.flagUserInitiatedThisSearch && !searchPack.flagFromMapClick) {
                const fallbackSearcherName = this.mapApp.getStateManager().getAdvancedSearchEnabled()
                    ? SEARCH_TYPES.ADVANCED
                    : SEARCH_TYPES.WHITE;
                const queryToRerun = searchPack.query;
                // Defer so the current Reverse search chain unwinds before we clear and switch tabs.
                queueMicrotask(() => {
                    this.setInputValue('');
                    this.updateClearButtonVisibility();
                    this.mapApp.programmaticallyRunSearchOnQuery(fallbackSearcherName, queryToRerun, false);
                });
                return buildEmptySearchResultStructure();
            }
            return buildEmptySearchResultStructure('format query as AA## (for example GV25)');
        }

        return await this.searchDataForJregionLoc(jregion, loclabel, searchPack);
    }


    async searchDataForJregionLoc(jregion, loclabel, searchPack) {
        //
        const results = [];
        let totalCount = 0;

        let error = preProcessQueryParseOptionsDirectives(searchPack);
        if (error) return buildEmptySearchResultStructure(error);

        // get data source (will also randomize if random option is set)
        const dataSource = this.getPoiDataManager().getDataSourceForSearch(searchPack);

        // destructure searchPack now, AFTER we have called preProcessQueryParseOptionsDirectives
        const limit = searchPack.limit;

        // much match BOTH fields (maybe call a more generic funciton that matches ALL fields)
        for (const item of dataSource) {
            if (item.jregion == jregion && item.loclabel == loclabel) {
                totalCount++;
                if (results.length < limit) {
                    results.push(item);
                }
            }
        }

        // return it as a SearchResult
        return buildSearchResult(results, totalCount);
    }



    /**
     * Converts the current reverse search into an equivalent advanced search query.
     * Builds a jregionloc equality expression and runs it as an advanced search.
     * @override
     */
    makeAdvancedSearch() {
        const searchPack = this.getLastSearchPack();
        const thisQueryStr = searchPack.query;
        if (!thisQueryStr) {
            return;
        }
        const { jregion, loclabel } = parseBlockQueryString(thisQueryStr);
        // split to canonical jregionloc
        const searchQuery = `(jregionloc == "${jregion}${loclabel}")`;
        const searchType = SEARCH_TYPES.ADVANCED;
        this.mapApp.programmaticallyRunSearchOnQuery(searchType, searchQuery, false);
    }


    /**
     * Sorts results for reverse directory display by address.
     * @param {Array} results - The results to sort
     * @returns {Array} The sorted results
     * @override
     */
    sortResults(results) {
        return this.sortResultsByField(results, 'sort_a', 'address');
    }





    // ==========================================
    // Post-Search Operations
    // ==========================================

    /**
     * Performs operations after completing a search.
     * Handles history saving based on search type and user action.
     * Saves immediately on Enter key; schedules delayed save otherwise.
     * @param {Object} searchPack - The search pack with query, isEnterKey, flagUserInitiatedThisSearch, explicitLimit, totalCount
     * @override
     */
    performPostSearchOperations(searchPack) {
        // destructure searchPack
        const {query, isEnterKey, flagUserInitiatedThisSearch, flagFromMapClick, explicitLimit, totalCount} = searchPack;
        const results = this.getListerResults();
        // fit the view to the results -- only when there ARE results: with none the fit falls back to
        // the region or the initial bounds, so a mistyped block (or a query redirected to the
        // Advanced tab, which leaves this tab empty) used to reset the map view
        if (isEnterKey && flagUserInitiatedThisSearch && !flagFromMapClick && results && results.length > 0) {
            this.getMapApp().autoZoomPanToResultSet(results);
        }

        // call super class
        super.performPostSearchOperations(searchPack);
    }
}
